<template>
    <div class="flex flex-col gap-4" data-test="rnd-run-controls">
        <div v-if="shownRun !== null" class="flex flex-col gap-1 text-sm">
            <div class="flex flex-wrap items-center gap-2">
                <span class="font-bold">{{ runActive ? 'Current run' : 'Latest run' }}</span>
                <span class="text-slate-500" data-test="shown-run-id">#{{ shownRun.id }}</span>
                <kiosk-rnd-run-status :run="shownRun" />
                <span class="text-slate-500" data-test="shown-run-date">
                    {{ runDateLabel(shownRun.finished_at ?? shownRun.snapshot_at) }}
                </span>
            </div>
            <div v-if="shownRun.counts !== null" class="text-slate-700" data-test="shown-run-progress">
                {{ contactsLabel(shownRun.counts.snapshot) }} · Queried {{ countLabel(shownRun.counts.queried) }} ·
                Reassigned {{ countLabel(shownRun.counts.reassigned) }} · Reset
                {{ countLabel(shownRun.counts.reassigned_breakdown.reset) }} · Not checked
                {{ countLabel(shownRun.counts.not_checked) }}
            </div>
        </div>
        <div v-else class="text-sm text-slate-500" data-test="no-runs">No runs yet.</div>

        <div class="flex flex-wrap gap-2">
            <vx-button
                type="button"
                data-test="run-check-now"
                color="primary"
                :disabled="!startable"
                @click="startModalVisible = true"
            >
                Run check now
                <font-awesome-icon widthAuto :icon="faPlay" />
            </vx-button>
            <vx-button
                v-if="reviewableRun !== null"
                type="button"
                data-test="review-run"
                color="primary"
                outline
                @click="openReview()"
            >
                Review resets
                <font-awesome-icon widthAuto :icon="faListCheck" />
            </vx-button>
            <vx-button
                v-if="cancellableRun !== null"
                type="button"
                data-test="cancel-run"
                color="danger"
                outline
                @click="cancelModalVisible = true"
            >
                Cancel run
                <font-awesome-icon widthAuto :icon="faBan" />
            </vx-button>
            <vx-button
                v-if="props.followableRun !== null"
                type="button"
                data-test="check-remaining"
                color="muted-dark"
                :disabled="props.companyId === null"
                @click="followUpModalVisible = true"
            >
                Check remaining contacts
                <font-awesome-icon widthAuto :icon="faRotateRight" />
            </vx-button>
        </div>

        <kiosk-rnd-run-start-modal
            v-if="props.companyId !== null"
            v-model:visible="startModalVisible"
            :teamId="props.teamId"
            :teamName="props.teamName"
            :companyId="props.companyId"
            :subscriberCount="props.subscriberCount"
            :supersededRun="supersededRun"
            @started="emit('changed')"
            @conflict="emit('changed')"
        />
        <kiosk-rnd-run-cancel-modal
            v-if="cancellableRun !== null"
            v-model:visible="cancelModalVisible"
            :run="cancellableRun"
            @cancelled="emit('changed')"
            @conflict="emit('changed')"
        />
        <kiosk-rnd-run-follow-up-modal
            v-if="props.followableRun !== null"
            v-model:visible="followUpModalVisible"
            :run="props.followableRun"
            @started="emit('changed')"
            @conflict="emit('changed')"
        />
        <kiosk-rnd-run-modal v-model:runId="reviewRunId" @changed="emit('changed')" />
    </div>
</template>

<script lang="ts" setup>
import { faBan, faListCheck, faPlay, faRotateRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton } from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import {
    canCancelRun,
    canReviewRun,
    contactsLabel,
    countLabel,
    runDateLabel,
} from '~/components/kiosk/kiosk-reassigned-numbers/rndLabels';
import KioskRndRunCancelModal from '~/components/kiosk/kiosk-reassigned-numbers/runs/KioskRndRunCancelModal.vue';
import KioskRndRunFollowUpModal from '~/components/kiosk/kiosk-reassigned-numbers/runs/KioskRndRunFollowUpModal.vue';
import KioskRndRunModal from '~/components/kiosk/kiosk-reassigned-numbers/runs/KioskRndRunModal.vue';
import KioskRndRunStartModal from '~/components/kiosk/kiosk-reassigned-numbers/runs/KioskRndRunStartModal.vue';
import KioskRndRunStatus from '~/components/kiosk/kiosk-reassigned-numbers/runs/KioskRndRunStatus.vue';
import type { RndRun } from '~/services/adminRndClient.types';

const props = defineProps<{
    teamId: number;
    teamName: string;
    companyId: string | null;
    subscriberCount: number;
    latestRun: RndRun | null;
    activeRun: RndRun | null;
    followableRun: RndRun | null;
}>();
const emit = defineEmits<{ changed: [] }>();

const startModalVisible = ref(false);
const cancelModalVisible = ref(false);
const followUpModalVisible = ref(false);
const reviewRunId = ref<number | null>(null);

const shownRun = computed(() => props.activeRun ?? props.latestRun);
const runActive = computed(() => props.activeRun !== null);
const startable = computed(() => props.companyId !== null && !runActive.value);
const cancellableRun = computed(() =>
    shownRun.value !== null && canCancelRun(shownRun.value) ? shownRun.value : null
);
const reviewableRun = computed(() =>
    props.latestRun !== null && canReviewRun(props.latestRun) ? props.latestRun : null
);
const supersededRun = computed(() => props.followableRun ?? reviewableRun.value);

const openReview = () => {
    reviewRunId.value = reviewableRun.value?.id ?? null;
};

watch(
    () => cancellableRun.value?.id,
    () => {
        cancelModalVisible.value = false;
    }
);

watch(
    () => props.followableRun?.id,
    () => {
        followUpModalVisible.value = false;
    }
);
</script>
